import { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";

import { useHistory, useLocation } from "react-router-dom";

import useAppLogin from "hooks/useAppLogin";
import { getOAuthErrorMessage, OAUTH_ERROR_CODES } from "utils/oauthErrorCodes";
import { handleAuthorizationCodeAuthentication } from "utils/oauth";
import { getUser } from "api/endpoints/AnalyticsApi";

const REACT_APP_SSO_ADDITIONAL_REDIRECT_PATTERN =
  process.env.REACT_APP_SSO_ADDITIONAL_REDIRECT_PATTERN;

const additionalRedirectPattern = REACT_APP_SSO_ADDITIONAL_REDIRECT_PATTERN
  ? new RegExp(REACT_APP_SSO_ADDITIONAL_REDIRECT_PATTERN)
  : undefined;

function OAuthLoginCallback() {
  const history = useHistory();
  const { set: setLoginData } = useAppLogin();
  let redirectUrl;

  // Get the code from the query params
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const code = queryParams.get("code");
  const error = queryParams.get("error");
  const stateBase64 = queryParams.get("state");

  if (stateBase64) {
    const { origin, ...remainingState } = JSON.parse(atob(stateBase64));
    if (origin) {
      const currentHost = window.location.host;
      const originatingHost = new URL(origin).host;

      const additionalRedirect = `${origin}/sso_callback`;

      if (
        currentHost !== originatingHost &&
        additionalRedirectPattern &&
        additionalRedirect.match(additionalRedirectPattern)
      ) {
        const remainingStateBase64 = btoa(JSON.stringify(remainingState));
        redirectUrl = new URL(additionalRedirect);
        redirectUrl.searchParams.set("code", code);
        redirectUrl.searchParams.set("error", error);
        redirectUrl.searchParams.set("state", remainingStateBase64);
        window.location.replace(redirectUrl.toString());
      }
    }
  }

  const hasRun = useRef(false); // Ref to track if the useEffect has run to avoid multiple calls

  useEffect(() => {
    if (redirectUrl) {
      window.location.replace(redirectUrl.toString());
    }
  }, [redirectUrl]);

  useEffect(() => {
    if (code && !hasRun.current && !redirectUrl) {
      handleAuthentication(code);
      hasRun.current = true;
    }
  }, [code]);

  // If there is an error, redirect to the login page
  useEffect(() => {
    if (error && !redirectUrl) {
      const errorToSearch = queryParams.get("error_description") || error;
      const errorMessage = getOAuthErrorMessage(errorToSearch);
      history.push(`/login?error=${errorMessage}`);
    }
  }, [error]);

  const handleAuthentication = async (code) => {
    try {
      await handleAuthorizationCodeAuthentication(code);
      const user = await getUser();
      setLoginData(user);

      // Redirect to the dashboard
      history.push("/dashboard");
    } catch (error) {
      console.error("Error handling Authorization Code authentication", error);
      history.push("/login?error=" + OAUTH_ERROR_CODES.DEFAULT);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="80vh"
    >
      <Typography variant="h5">Authentication...</Typography>
    </Box>
  );
}

export default OAuthLoginCallback;
